import './SelectTQConnectTags.scss';

import React, {
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { Checkbox, Input } from 'antd';

import Loader from '../../../../components/V2/Loader/Loader';
import {
  useTQConnectTags,
} from '../../../../hooks/queries/tqConnectQueries/service-requests';
import { useDebounce } from '../../../../shared/hooks';
import { PAGE_LIMIT } from '../../constants';
import { ITQTagSelectOption } from './types';
import { ITQTagItem } from '../../../../types/tqConnectTypes/service-requests';

export interface SelectTQConnectTagsProps {
  value?: ITQTagSelectOption[];
  onChange?: (value: ITQTagSelectOption[]) => void;
  debounceTimeout?: number;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const toOption = (tag: ITQTagItem | ITQTagSelectOption): ITQTagSelectOption => ({
  ...tag,
  key: tag._id,
  label: tag.name,
  value: tag._id,
});

const SelectTQConnectTags: React.FC<SelectTQConnectTagsProps> = ({
  debounceTimeout = 1000,
  value: selectedTags = [],
  onChange,
  placeholder = 'Search tags...',
  className,
  disabled,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSearchQuery = useDebounce(searchQuery, debounceTimeout);

  const { data: tags, isLoading: isLoadingTags } = useTQConnectTags({
    search: debouncedSearchQuery,
    limit: PAGE_LIMIT,
    page: 1,
  });

  const selectedIds = useMemo(
    () => new Set(selectedTags.map(t => t._id)),
    [selectedTags],
  );

  const options = useMemo(() => {
    return [
      ...selectedTags.map(toOption),
      ...(tags || [])
        .filter(tag => !selectedIds.has(tag._id))
        .map(toOption),
    ];
  }, [tags, selectedTags, selectedIds]);

  const handleToggle = useCallback(
    (tag: ITQTagSelectOption, checked: boolean) => {
      if (checked) {
        onChange?.([...selectedTags, tag]);
      } else {
        onChange?.(selectedTags.filter(t => t._id !== tag._id));
      }
    },
    [selectedTags, onChange],
  );

  return (
    <div className={`select-tq-connect-tags ${className ?? ''}`.trim()}>
      <Input
        size="large"
        placeholder={placeholder}
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        disabled={disabled}
        allowClear
        className="select-tq-connect-tags__search"
      />
      <div className="select-tq-connect-tags__list">
        {isLoadingTags ? (
          <div className="select-tq-connect-tags__loader">
            <Loader type="dots" />
          </div>
        ) : options.length === 0 ? (
          <div className="select-tq-connect-tags__empty">
            {searchQuery ? 'No tags found' : 'Search tags...'}
          </div>
        ) : (
          options.map(tag => (
            <Checkbox
              key={tag._id}
              checked={selectedIds.has(tag._id)}
              onChange={e => handleToggle(tag, e.target.checked)}
              disabled={disabled}
              className="select-tq-connect-tags__checkbox"
            >
              {tag.name}
            </Checkbox>
          ))
        )}
      </div>
    </div>
  );
};

export default memo(SelectTQConnectTags);
